import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import type { ApolloError } from '@apollo/client';
import { convertTo62, convertToUUID } from 'uuid-encoding';
import { useVisitorCustomFieldConfigs } from 'domains/visitorManagement/hooks/useVisitorCustomFieldConfigs';
import type { VisitorConfigurationTabProps } from '../../types';
import type { CustomFieldDetailModel } from './types';
import { mapQueryConfigsToDetailRows } from './mapCustomFieldConfigRow';
import { filterCustomFieldDetailRowsBySearch } from './searchFilter';
import { useCustomFieldStatusUpdate } from './useCustomFieldStatusUpdate';

export type CustomFieldsListLayoutProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  filteredRows: readonly CustomFieldDetailModel[];
  isListLoading: boolean;
  configsCalled: boolean;
  configsError: ApolloError | undefined;
  onCreateClick: () => void;
  onSelectRow: (row: CustomFieldDetailModel) => void;
  onToggleStatus: (row: CustomFieldDetailModel) => Promise<boolean>;
  isStatusUpdateLoading: boolean;
};

export function useCustomFieldsTabModel({
  channel,
}: Pick<VisitorConfigurationTabProps, 'channel'>) {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const organizationId = channel?._id ? convertToUUID(channel._id) : null;

  const {
    data: customFieldConfigs,
    loading: configsLoading,
    error: configsError,
    called: configsCalled,
    fetch: fetchConfigs,
  } = useVisitorCustomFieldConfigs();

  useEffect(() => {
    if (!organizationId) return;

    fetchConfigs(organizationId);
  }, [organizationId, fetchConfigs]);

  const detailRows = useMemo(
    () => mapQueryConfigsToDetailRows(customFieldConfigs ?? []),
    [customFieldConfigs]
  );

  const filteredRows = useMemo(
    () => filterCustomFieldDetailRowsBySearch(detailRows, searchValue, t),
    [detailRows, searchValue, t]
  );

  const isListLoading = detailRows.length === 0 && configsLoading;

  const {
    handleToggleStatus,
    handleDisableConfirm,
    handleDisableCancel,
    pendingDisableRow,
    isLoading,
  } = useCustomFieldStatusUpdate({ organizationId, customFieldConfigs });

  const navigateToCreate = useCallback(() => {
    history.push(`${pathname}/custom-fields/new`);
  }, [history, pathname]);

  const navigateToDetail = useCallback(
    (row: CustomFieldDetailModel) => {
      history.push(`${pathname}/custom-fields/${convertTo62(row.id)}`);
    },
    [history, pathname]
  );

  return {
    t,
    navigateToCreate,
    navigateToDetail,
    searchValue,
    setSearchValue,
    filteredRows,
    isListLoading,
    configsCalled,
    configsError,
    handleToggleStatus,
    handleDisableConfirm,
    handleDisableCancel,
    pendingDisableRow,
    isStatusUpdateLoading: isLoading,
  };
}
